import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StreamPlayer from './StreamPlayer';
import { streamThumb, streamPreview } from '../utils/cloudflare';
import { useTranslation } from '../LanguageContext';

/**
 * One media surface for the whole product: the creator's own gallery, the
 * public profile grid and the press page all used to carry their own copy of
 * "square div, cover image, play circle, black overlay". Three copies meant
 * three different sets of rounded corners and one shared bug — the viewer
 * forced every video into a 9:16 box, so a landscape upload was published as
 * a letterboxed sliver of itself.
 *
 * Everything here is presentational. Uploading, deleting and permissions stay
 * with whoever owns the data.
 */

export const isPlayable = (item) => item?.type === 'video' || item?.type === 'photo';

/** The still for a tile — a Stream frame for video, the image itself for a photo. */
export function mediaThumb(item, { height } = {}) {
    if (!item?.url) return '';
    return item.type === 'video' ? streamThumb(item.url, { height }) : item.url;
}

const PlayGlyph = ({ className = '' }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M8 5v14l11-7z" />
    </svg>
);

const ASPECTS = {
    square: 'aspect-square',
    portrait: 'aspect-[4/5]',
    wide: 'aspect-video',
};

/**
 * A single tile. The video thumbnail swaps to Stream's animated preview on
 * hover — the strip is a separate render on Cloudflare's side, so it is only
 * fetched once someone actually points at the tile, never for a whole grid.
 */
export function MediaTile({
    item,
    onOpen,
    aspect = 'square',
    rounded = 'rounded-2xl',
    children,
    label,
    className = '',
}) {
    const { t } = useTranslation();
    const [broken, setBroken] = useState(false);
    const [hovered, setHovered] = useState(false);
    const isVideo = item.type === 'video';

    return (
        <div
            role={onOpen ? 'button' : undefined}
            tabIndex={onOpen ? 0 : undefined}
            aria-label={label || (isVideo ? t('media.video') : t('media.photo'))}
            onClick={onOpen}
            onKeyDown={onOpen ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
            } : undefined}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`group relative ${ASPECTS[aspect] || ASPECTS.square} ${rounded} overflow-hidden
                bg-slate-900 ring-1 ring-slate-900/5 shadow-sm
                transition-[transform,box-shadow] duration-300 ease-out
                ${onOpen ? 'cursor-pointer hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2' : ''}
                ${className}`}
        >
            {!broken ? (
                <img
                    src={mediaThumb(item, { height: 640 })}
                    alt=""
                    loading="lazy"
                    draggable={false}
                    onError={() => setBroken(true)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
            )}

            {/* Hover motion, on intent only. */}
            {isVideo && hovered && !broken && (
                <img
                    src={streamPreview(item.url)}
                    alt=""
                    draggable={false}
                    className="absolute inset-0 w-full h-full object-cover media-preview-in"
                />
            )}

            {/* A readable floor for anything printed over the image. */}
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

            {isVideo && (
                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md ring-1 ring-white/25 shadow-lg
                        flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                        <PlayGlyph className="w-4 h-4 text-white ml-0.5" />
                    </span>
                </span>
            )}

            {children}
        </div>
    );
}

/** The type pill a tile carries in its corner. */
export function MediaTypeBadge({ type, className = '' }) {
    const { t } = useTranslation();
    return (
        <span className={`pointer-events-none inline-flex items-center gap-1 rounded-full
            bg-black/45 backdrop-blur-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white/90
            ring-1 ring-white/15 ${className}`}>
            {type === 'video'
                ? <PlayGlyph className="w-2.5 h-2.5" />
                : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-2.5 h-2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                )}
            {type === 'video' ? t('media.video') : t('media.photo')}
        </span>
    );
}

/**
 * The full-screen viewer.
 *
 * `items` is the whole set so the arrows, the counter and the filmstrip have
 * something to move through — opening one photo used to be a dead end, which
 * is why nobody looked past the first tile.
 *
 * The frame is sized from the media's *own* aspect ratio (probed from the
 * thumbnail, then confirmed by the video's metadata) rather than from a
 * hardcoded 9:16, so a landscape video fills the width and a portrait one
 * fills the height.
 */
/**
 * How much of the bottom of a video frame the player's own controls own —
 * play/pause, the scrubber and the volume. Chrome desktop draws about 40px,
 * mobile Safari and Android Chrome closer to 50; this is that plus room to
 * aim on a touchscreen.
 */
const PLAYER_CONTROLS_PX = 64;

export function MediaLightbox({ items, index, onIndex, onClose }) {
    const { t } = useTranslation();
    // Memoised because the neighbour-preload effect keys off it: a new array
    // every render would mint a fresh Image on every render too.
    const list = useMemo(() => (items || []).filter(isPlayable), [items]);
    const safeIndex = Math.min(Math.max(index ?? 0, 0), Math.max(list.length - 1, 0));
    const item = list[safeIndex];
    const [ratio, setRatio] = useState(null);
    const [chrome, setChrome] = useState(true);
    // The frame is sized in viewport units, so where it ends is a question only
    // the viewport can answer — and the filmstrip has to be placed against it.
    const [viewport, setViewport] = useState(() => ({
        width: typeof window === 'undefined' ? 0 : window.innerWidth,
        height: typeof window === 'undefined' ? 0 : window.innerHeight,
    }));
    const touchStart = useRef(null);
    const idleTimer = useRef(null);

    /** Show the chrome and start counting down again. */
    const wake = useCallback(() => {
        setChrome(true);
        if (idleTimer.current) clearTimeout(idleTimer.current);
        idleTimer.current = setTimeout(() => setChrome(false), 2800);
    }, []);

    /** Pointer resting on a control: it stays until the pointer leaves. */
    const hold = useCallback(() => {
        setChrome(true);
        if (idleTimer.current) clearTimeout(idleTimer.current);
    }, []);

    const go = useCallback((delta) => {
        if (list.length < 2) return;
        const next = (safeIndex + delta + list.length) % list.length;
        setRatio(null);
        onIndex?.(next);
    }, [list.length, safeIndex, onIndex]);

    // Probe the still for the real shape. It carries the same dimensions as
    // the media and is already cached from the tile, so the frame is correct
    // before a single video byte arrives.
    useEffect(() => {
        if (!item) return;
        let live = true;
        const img = new Image();
        img.onload = () => {
            if (live && img.naturalWidth && img.naturalHeight) {
                setRatio(img.naturalWidth / img.naturalHeight);
            }
        };
        img.src = mediaThumb(item);
        return () => { live = false; };
    }, [item]);

    // A phone rotated mid-video re-letterboxes the frame, which moves the strip
    // the controls live in.
    useEffect(() => {
        const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, []);

    // Neighbours, so paging through a gallery does not flash empty.
    useEffect(() => {
        if (list.length < 2) return;
        [-1, 1].forEach((delta) => {
            const neighbour = list[(safeIndex + delta + list.length) % list.length];
            if (neighbour) new Image().src = mediaThumb(neighbour, { height: 640 });
        });
    }, [list, safeIndex]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose?.();
            else if (e.key === 'ArrowRight') { wake(); go(1); }
            else if (e.key === 'ArrowLeft') { wake(); go(-1); }
        };
        window.addEventListener('keydown', onKey);
        wake();

        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            if (idleTimer.current) clearTimeout(idleTimer.current);
            document.body.style.overflow = previous;
        };
    }, [go, onClose, wake]);

    if (!item) return null;

    const isVideo = item.type === 'video';
    const shape = ratio || (isVideo ? 16 / 9 : 1);
    // The media takes the whole screen, so the frame is the largest box of the
    // right shape that fits the viewport. One axis has to be definite —
    // leaving both `auto` next to an aspect-ratio lets the constraints fight
    // and the frame jumps as the video loads.
    const frameStyle = { aspectRatio: shape, width: `min(100vw, calc(100vh * ${shape}))` };

    // The chrome floats over the picture rather than taking a strip of it, so
    // it has to get out of the way: it fades out after a few idle seconds and
    // returns on any movement. Otherwise the filmstrip sits exactly where the
    // player draws its own controls.
    const chromeClass = chrome ? 'opacity-100' : 'opacity-0 pointer-events-none';

    // The player draws its controls along the bottom of the *frame*; the
    // filmstrip is anchored to the bottom of the *screen*. Whenever the frame
    // reaches the screen's floor those are the same strip, and every attempt to
    // hit play/pause or the volume landed on a thumbnail instead. Fading does
    // not rescue it: the movement or tap that reveals the player's controls is
    // exactly the movement that wakes the chrome back up, so the two arrive
    // together every time. Letterboxing already clears the controls on a wide
    // video in a tall window — so only make up the difference.
    const frameHeight = Math.min(viewport.height, viewport.width / shape);
    const controlsClearance = isVideo
        ? Math.round(Math.max(0, PLAYER_CONTROLS_PX - (viewport.height - frameHeight) / 2))
        : 0;

    // Rendered into <body>, never in place. `position: fixed` is relative to
    // the nearest ancestor with a transform, filter or **backdrop-filter** —
    // and the public profile puts this gallery inside a `backdrop-blur-xl`
    // card, which pinned the "full screen" viewer inside a 448 px column.
    // Nothing about the viewer belongs to the page it was opened from.
    return createPortal(
        <div
            className="fixed inset-0 z-[9999] bg-black media-scrim"
            onClick={onClose}
            onMouseMove={wake}
            onTouchStart={(e) => { wake(); touchStart.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
                if (touchStart.current === null) return;
                const dx = e.changedTouches[0].clientX - touchStart.current;
                if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
                touchStart.current = null;
            }}
            role="dialog"
            aria-modal="true"
        >
            {/* Stage — the whole screen. */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div
                    key={item.id || item.url}
                    className="relative max-h-full max-w-full flex items-center justify-center media-stage"
                    onClick={(e) => { e.stopPropagation(); wake(); }}
                >
                    {isVideo ? (
                        <div
                            style={frameStyle}
                            // Mirrored as an attribute because `aspect-ratio`
                            // is what this frame is *for*, and a style object
                            // is not something a test can read back.
                            data-aspect={shape.toFixed(3)}
                            className="relative max-h-full bg-black"
                        >
                            <StreamPlayer
                                videoUrl={item.url}
                                autoPlay
                                controls
                                poster={mediaThumb(item)}
                                className="w-full h-full bg-black"
                                style={{ objectFit: 'contain' }}
                                onLoadedMetadata={(e) => {
                                    const { videoWidth, videoHeight } = e.target;
                                    if (videoWidth && videoHeight) setRatio(videoWidth / videoHeight);
                                }}
                            />
                        </div>
                    ) : (
                        <img
                            src={item.url}
                            alt=""
                            draggable={false}
                            className="max-h-screen max-w-full object-contain"
                        />
                    )}
                </div>
            </div>

            {/* Top bar */}
            <div
                className={`absolute top-0 inset-x-0 z-20 flex items-center justify-between gap-3
                    px-4 sm:px-6 py-3 bg-gradient-to-b from-black/70 to-transparent
                    transition-opacity duration-300 ${chromeClass}`}
                onClick={(e) => e.stopPropagation()}
                onMouseMove={(e) => { e.stopPropagation(); hold(); }}
                onMouseLeave={wake}
            >
                <div className="flex items-center gap-2.5 min-w-0">
                    <MediaTypeBadge type={item.type} className="!bg-white/10" />
                    {list.length > 1 && (
                        <span className="text-xs font-bold text-white/70 tabular-nums">
                            {safeIndex + 1} / {list.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {!isVideo && (
                        <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t('media.openOriginal')}
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition no-underline"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5M15 3h6m0 0v6m0-6L10.5 13.5" />
                            </svg>
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('media.close')}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Paging chrome: arrows and the filmstrip, on the same fade. */}
            {list.length > 1 && (
                <div className={`transition-opacity duration-300 ${chromeClass}`}
                    onMouseMove={(e) => { e.stopPropagation(); hold(); }}
                    onMouseLeave={wake}>
                    <NavArrow side="left" onClick={(e) => { e.stopPropagation(); go(-1); }} label={t('media.previous')} />
                    <NavArrow side="right" onClick={(e) => { e.stopPropagation(); go(1); }} label={t('media.next')} />

                    {/* The floor the thumbnails are read against. Kept separate
                        from the strip so the tall, soft part of it never
                        swallows a click meant for the picture behind it. */}
                    <div
                        style={{ height: 144 + controlsClearance }}
                        className="absolute bottom-0 inset-x-0 z-10 pointer-events-none
                            bg-gradient-to-t from-black/85 via-black/45 to-transparent" />

                    <div
                        style={{ bottom: controlsClearance }}
                        data-controls-clearance={controlsClearance}
                        className="absolute inset-x-0 z-20 px-3 sm:px-6 py-2 overflow-x-auto"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-start sm:justify-center gap-2 min-w-min">
                            {list.map((row, i) => (
                                <button
                                    key={row.id || row.url}
                                    type="button"
                                    onClick={() => { if (i !== safeIndex) { setRatio(null); onIndex?.(i); } }}
                                    className={`relative w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-xl overflow-hidden transition
                                        ${i === safeIndex
                                            ? 'ring-2 ring-primary-400 opacity-100 scale-105'
                                            : 'ring-1 ring-white/15 opacity-50 hover:opacity-90'}`}
                                >
                                    <img src={mediaThumb(row, { height: 160 })} alt="" className="w-full h-full object-cover" />
                                    {row.type === 'video' && (
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                                            <PlayGlyph className="w-3 h-3 text-white" />
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>,
        document.body,
    );
}

function NavArrow({ side, onClick, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={`absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full
                bg-white/10 hover:bg-white/20 backdrop-blur-md text-white
                flex items-center justify-center transition
                ${side === 'left' ? 'left-1 sm:left-4' : 'right-1 sm:right-4'}`}
        >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round"
                    d={side === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
            </svg>
        </button>
    );
}

/**
 * Grid + viewer in one, for the read-only surfaces (public profile, press
 * page). State for "which one is open" lives here so a caller only passes the
 * list.
 */
export default function MediaGallery({
    items,
    columns = 'grid-cols-3',
    aspect = 'square',
    gap = 'gap-1.5',
    rounded = 'rounded-2xl',
    renderBadge,
    onOpenChange,
}) {
    const [openAt, setOpenAt] = useState(null);
    const playable = useMemo(() => (items || []).filter(isPlayable), [items]);

    // Callers sometimes park expensive scenery behind the page (the profile's
    // blurred cover) and want it out of the way while the viewer is up.
    useEffect(() => { onOpenChange?.(openAt !== null); }, [openAt, onOpenChange]);

    if (playable.length === 0) return null;

    return (
        <>
            <div className={`grid ${columns} ${gap}`}>
                {playable.map((item, i) => (
                    <MediaTile
                        key={item.id || item.url}
                        item={item}
                        aspect={aspect}
                        rounded={rounded}
                        onOpen={() => setOpenAt(i)}
                    >
                        {renderBadge?.(item)}
                    </MediaTile>
                ))}
            </div>

            {openAt !== null && (
                <MediaLightbox
                    items={playable}
                    index={openAt}
                    onIndex={setOpenAt}
                    onClose={() => setOpenAt(null)}
                />
            )}
        </>
    );
}
